import {
  Divider,
  Grid,
  HStack,
  Text,
  useBoolean,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { Decimal } from 'decimal.js';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { ListUtils } from '../../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import StyledButton from '../../../../components/StyledButton';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import { useFormItems } from '../../../../hooks/formItems';
import { Formatter } from '../../../../utils/FormatUtils';
import { useMasterContext } from '../../../MasterProvider';
import { MatterDetailModal } from '../../../modals/MatterDetail';
import { AssignWorkInfoDaySearchResultForm } from '../WorkContent';
import { AssignWorkInfoProps } from './AssignWorkInfo';
import { GridItem } from './GridItem';

/**
 *
 */
type MattersProps = AssignWorkInfoProps & {
  /**
   * 欠勤フラグ
   */
  isAbsence?: boolean;
  matterIndex?: number;
  /**
   * 現場数
   */
  matterSize: number;
  toggle: Runnable;
  onChangedRelatedToSavable: () => void;
  onChangedRelatedToDailyWork: () => void;
};

/**
 *
 * @returns
 */
const Matters: React.FunctionComponent<MattersProps> = (props) => {
  const { state } = useGlobalStateMachine();
  const master = useMasterContext();
  const ATTENDANCE_TYPE_PRESENT = React.useMemo<number>(
    () => state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_WORK as number,
    [state.appConfig.facilitySettings]
  );
  const absent = React.useMemo<number>(
    () =>
      state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_ABSENCE as number,
    [state.appConfig.facilitySettings]
  );
  const [matterNo, setMatterNo] = React.useState(props.matterIndex || 0);
  const form = useFormContext<AssignWorkInfoDaySearchResultForm>();
  const {
    FormCheckbox,
    FormDatePicker,
    FormInput,
    FormInputNumber,
    FormMultiSelect,
    FormSelect,
    FormText,
    FormTextarea,
  } = useFormItems(form);
  const [selectedMasterId, setSelectedMatterId] = React.useState<number>();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [recalculateMatterTrigger, { toggle: recalculateMatter }] =
    useBoolean(false);

  React.useEffect(() => {
    Array(props.matterSize)
      .fill(null)
      .forEach(
        (_, index) =>
          props.isAbsence &&
          form.setValue(
            `results.${index}.assignedWorkerWorkInfo.attendanceTypeId`,
            absent
          )
      );
  }, [props.isAbsence]);

  const workTimeFrom = React.useMemo(
    () =>
      form.getValues(`results.${matterNo}.assignedWorkerWorkInfo.workTimeFrom`),
    [form, matterNo, recalculateMatterTrigger]
  );
  const workTimeTo = React.useMemo(
    () =>
      form.getValues(`results.${matterNo}.assignedWorkerWorkInfo.workTimeTo`),
    [form, matterNo, recalculateMatterTrigger]
  );
  const workTime = React.useMemo(() => {
    // 作業時間を再計算して setValue する
    const from =
      form.getValues(
        `results.${matterNo}.assignedWorkerWorkInfo.workTimeFrom`
      ) || '';
    const to =
      form.getValues(`results.${matterNo}.assignedWorkerWorkInfo.workTimeTo`) ||
      '';
    const rest =
      form.getValues(`results.${matterNo}.assignedWorkerWorkInfo.restTime`) ||
      0;
    const wt = (Date.parse(to) - Date.parse(from)) / (1000 * 60) - rest;
    form.setValue(`results.${matterNo}.assignedWorkerWorkInfo.workTime`, wt);
    // ここで DailyWork 側変更を通知
    props.onChangedRelatedToDailyWork();
    return (
      form.getValues(`results.${matterNo}.assignedWorkerWorkInfo.workTime`) || 0
    );
  }, [form, matterNo, recalculateMatterTrigger]);
  const attendanceTypeId = React.useMemo(
    () =>
      form.getValues(
        `results.${matterNo}.assignedWorkerWorkInfo.attendanceTypeId`
      ),
    [form, matterNo, recalculateMatterTrigger]
  );
  React.useEffect(() => {
    if (props.isAbsence && attendanceTypeId !== absent) {
      props.toggle();
    }
  }, [attendanceTypeId]);

  // 現場毎に isOvertime を考慮する
  // 残業有無(総作業時間が8時間を越える)
  const isOvertime = React.useMemo(
    () =>
      workTime > 480 &&
      !TypeUtils.isNilString(workTimeFrom) &&
      !TypeUtils.isNilString(workTimeTo),
    [workTime]
  );

  return (
    <VStack width="100%">
      <HStack
        height={{ base: '20px', md: 'unset' }}
        justifyContent="space-between"
      >
        <StyledButton
          fontWeight="bold"
          height="full"
          isDisabled={matterNo === 0}
          onClick={() => setMatterNo(Math.max(matterNo - 1, 0))}
          textDecoration="unset"
          variant="link"
        >
          &lt;&lt;
        </StyledButton>
        <Text fontSize={{ base: 'md', md: '2xl' }}>{matterNo + 1} 現場目</Text>
        {/* { base: 'mobile.3xl', md: '2xl' } */}
        <StyledButton
          fontWeight="bold"
          height="full"
          isDisabled={matterNo === props.matterSize - 1}
          onClick={() =>
            setMatterNo(Math.min(matterNo + 1, props.matterSize - 1))
          }
          textDecoration="unset"
          variant="link"
        >
          &gt;&gt;
        </StyledButton>
      </HStack>
      <Grid
        backgroundColor="white"
        borderColor="gray.300"
        borderRadius="md"
        borderWidth={1}
        key={matterNo}
        padding={2}
        templateColumns={{ base: 'unset', md: '1fr 1fr' }}
        width="100%"
      >
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormText
            name={`results.${matterNo}.matterSummary.matterName`}
            value={(field) => (
              <StyledButton
                onClick={() => {
                  const matterId = form.getValues(
                    `results.${matterNo}.matterSummary.matterId`
                  );
                  if (!TypeUtils.isNilNumber(matterId)) {
                    setSelectedMatterId(matterId);
                    onOpen();
                  }
                }}
                textAlign="left"
                variant="link"
                whiteSpace="normal"
              >
                {field.value}
              </StyledButton>
            )}
          />
        </GridItem>
        <GridItem>
          <FormText
            label="依頼元"
            name={`results.${matterNo}.matterSummary.customerName`}
          />
        </GridItem>
        <GridItem>
          <FormText
            label="最寄駅"
            name={`results.${matterNo}.matterSummary.station`}
          />
        </GridItem>
        <GridItem>
          <FormText
            label="営業担当"
            name={`results.${matterNo}.matterSummary.nameSales`}
          />
        </GridItem>
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormCheckbox
            checkboxProps={{
              defaultChecked: form.getValues(`results.${matterNo}.isLeader`),
            }}
            mode="view"
            name={`results.${matterNo}.isLeader`}
          >
            職長
          </FormCheckbox>
        </GridItem>
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormMultiSelect
            label="作業"
            mode="view"
            name={`results.${matterNo}.assignedWorkerSummary.taskIds`}
            options={Object.entries(master.task || {})
              .sort(ListUtils.sort(([_, m]) => m.displayOrder))
              .map(([_, v]) => ({
                value: v.id,
                valueToDisplay: v.name,
              }))}
          />
        </GridItem>
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormText
            label="依頼時刻"
            name={`results.${matterNo}.assignedWorkerSummary.requestTime`}
            value={(field) => Formatter.time(field.value, '―')}
          />
        </GridItem>
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormText
            label="依頼時メモ"
            name={`results.${matterNo}.assignedWorkerSummary.requestMemo`}
          />
        </GridItem>

        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <Divider marginY={2} />
        </GridItem>

        <GridItem as={HStack}>
          <GridItem w="56%">
            <FormSelect<number>
              formControlProps={{ isRequired: true }}
              label="作業状況"
              mode={
                !TypeUtils.isNilString(
                  form.watch(
                    `results.${matterNo}.assignedWorkerSummary.reportApprovedTime`
                  )
                )
                  ? 'view'
                  : props.mode
              }
              name={`results.${matterNo}.assignedWorkerWorkInfo.attendanceTypeId`}
              options={Object.values(master.attendanceType || {})
                .sort(ListUtils.sort((m) => m.displayOrder))
                .filter((v) =>
                  (
                    (state.appConfig.facilitySettings
                      ?.WORKING_REPORT_ATTENDANCE_TYPE_IDS ||
                      []) as Array<number>
                  ).includes(v.id)
                )
                .map((v) => ({
                  value: v.id,
                  valueToDisplay: v.name,
                }))}
              valueConverter={parseInt}
            />
          </GridItem>
          <GridItem
            as={HStack}
            justifyContent="start"
            hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}
          >
            <FormCheckbox
              checkboxProps={{
                defaultChecked: form.getValues(
                  `results.${matterNo}.assignedWorkerWorkInfo.beLate`
                ),
              }}
              formControlProps={{
                width: 'unset',
              }}
              mode={
                !TypeUtils.isNilString(
                  form.watch(
                    `results.${matterNo}.assignedWorkerSummary.reportApprovedTime`
                  )
                )
                  ? 'view'
                  : props.mode
              }
              name={`results.${matterNo}.assignedWorkerWorkInfo.beLate`}
            >
              遅刻
            </FormCheckbox>
            <FormCheckbox
              checkboxProps={{
                defaultChecked: form.getValues(
                  `results.${matterNo}.assignedWorkerWorkInfo.leaveWork`
                ),
              }}
              formControlProps={{ width: 'unset' }}
              mode={
                !TypeUtils.isNilString(
                  form.watch(
                    `results.${matterNo}.assignedWorkerSummary.reportApprovedTime`
                  )
                )
                  ? 'view'
                  : props.mode
              }
              name={`results.${matterNo}.assignedWorkerWorkInfo.leaveWork`}
            >
              早退
            </FormCheckbox>
          </GridItem>
        </GridItem>

        <GridItem hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}>
          <FormDatePicker
            datePickerProps={{
              showTimeSelect: true,
              onCalendarClose: () => {
                recalculateMatter();
                props.onChangedRelatedToSavable();
              },
            }}
            formControlProps={{
              isRequired:
                form.watch(
                  `results.${matterNo}.assignedWorkerWorkInfo.attendanceTypeId`
                ) === ATTENDANCE_TYPE_PRESENT,
            }}
            label="開始時刻"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${matterNo}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${matterNo}.assignedWorkerWorkInfo.workTimeFrom`}
            onClickClear={(name) => {
              form.setValue(name, '');
              recalculateMatter();
              props.onChangedRelatedToSavable();
            }}
          />
        </GridItem>
        <GridItem hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}>
          <FormDatePicker
            datePickerProps={{
              showTimeSelect: true,
              onCalendarClose: () => {
                recalculateMatter();
                props.onChangedRelatedToDailyWork();
              },
            }}
            formControlProps={{
              isRequired:
                form.watch(
                  `results.${matterNo}.assignedWorkerWorkInfo.attendanceTypeId`
                ) === ATTENDANCE_TYPE_PRESENT,
            }}
            label="終了時刻"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${matterNo}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${matterNo}.assignedWorkerWorkInfo.workTimeTo`}
            onClickClear={(name) => {
              form.setValue(name, '');
              recalculateMatter();
              props.onChangedRelatedToSavable();
            }}
          />
        </GridItem>
        <GridItem hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}>
          <FormInputNumber
            formControlProps={{
              isRequired:
                form.watch(
                  `results.${matterNo}.assignedWorkerWorkInfo.attendanceTypeId`
                ) === ATTENDANCE_TYPE_PRESENT,
              onBlur: () => {
                recalculateMatter();
                props.onChangedRelatedToSavable();
              },
            }}
            label="休憩時間（分）"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${matterNo}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${matterNo}.assignedWorkerWorkInfo.restTime`}
            numberInputProps={{ min: 0 }}
            numberInputStepperProps={{ hidden: true }}
            viewNode={({ field }) => (
              <Text>{Formatter.workingTime(field.value)}</Text>
            )}
          />
        </GridItem>
        <GridItem hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}>
          <FormInputNumber
            label="追加作業時間（分）"
            name={`results.${matterNo}.assignedWorkerWorkInfo.restTime`}
            numberInputProps={{ style: { pointerEvents: 'none' } }}
            numberInputFieldProps={{ tabIndex: -1 }}
            readonly
            // value={(field) => Math.max(workTime - 480, 0)}
            viewNode={() => (
              <Text>{Formatter.workingTime(Math.max(workTime - 480, 0))}</Text>
            )}
          />
        </GridItem>
        {/* 外注は残業時間を現場ごとに計算 */}
        <GridItem
          hidden={
            attendanceTypeId !== ATTENDANCE_TYPE_PRESENT ||
            !state.userInfo?.roleOptions?.isOutsourcer
          }
        >
          <FormInputNumber
            label="残業時間（分）"
            name={`results.${matterNo}.assignedWorkerWorkInfo.restTime`}
            readonly
            numberInputProps={{ style: { pointerEvents: 'none' } }}
            numberInputFieldProps={{ tabIndex: -1 }}
            viewNode={({ field }) => {
              if (isOvertime && TypeUtils.isNonNilNumber(workTime)) {
                const from = Formatter.createDate(workTimeFrom!);
                const to = Formatter.createDate(workTimeTo!);
                const h22 = new Date(from);
                h22.setHours(22);
                h22.setMinutes(0);
                h22.setSeconds(0);
                h22.setMilliseconds(0);
                // 22時以前の労働時間のうち8時間を超えない部分だけ
                // 1 現場ごとだから from と to から労働時間を計算してよい
                return (
                  <Text>
                    {Formatter.workingTime(
                      Math.max(
                        new Decimal(Math.min(h22.valueOf(), to.valueOf()))
                          .minus(from.valueOf())
                          .minus(28800000)
                          .dividedToIntegerBy(60000)
                          .minus(field.value || 0)
                          .toNumber(),
                        0
                      )
                    )}
                  </Text>
                );
              }
              return <Text>{Formatter.workingTime(0)}</Text>;
            }}
          />
        </GridItem>
        <GridItem
          hidden={
            attendanceTypeId !== ATTENDANCE_TYPE_PRESENT ||
            !state.userInfo?.roleOptions?.isOutsourcer
          }
        >
          <FormInputNumber
            label="深夜残業時間（分）"
            name="reference.dailyWork"
            readonly
            numberInputProps={{ style: { pointerEvents: 'none' } }}
            numberInputFieldProps={{ tabIndex: -1 }}
            viewNode={() => {
              if (isOvertime) {
                const from = Formatter.createDate(workTimeFrom!);
                const to = Formatter.createDate(workTimeTo!);
                const h22 = new Date(from);
                h22.setHours(22);
                h22.setMinutes(0);
                h22.setSeconds(0);
                h22.setMilliseconds(0);
                // 労働時間のうち8時間を超えた分のうち22時以降
                return (
                  <Text>
                    {Formatter.workingTime(
                      Math.max(
                        Math.min(
                          workTime - 480,
                          new Decimal(to.valueOf())
                            .minus(h22.valueOf())
                            .dividedToIntegerBy(60000)
                            .toNumber()
                        ),
                        0
                      )
                    )}
                  </Text>
                );
              }
              return <Text>{Formatter.workingTime(0)}</Text>;
            }}
          />
        </GridItem>
        <GridItem
          hidden={
            attendanceTypeId !== ATTENDANCE_TYPE_PRESENT ||
            (attendanceTypeId === ATTENDANCE_TYPE_PRESENT && !isOvertime)
          }
        >
          <FormDatePicker
            datePickerProps={{
              showTimeSelect: true,
              onCalendarClose: () => {
                props.onChangedRelatedToSavable();
              },
            }}
            formControlProps={{ isRequired: isOvertime }}
            label="残業承認時刻"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${matterNo}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${matterNo}.assignedWorkerWorkInfo.overtimeApprovedTime`}
            onClickClear={(name) => {
              form.setValue(name, '');
              props.onChangedRelatedToSavable();
            }}
          />
        </GridItem>
        <GridItem
          hidden={
            attendanceTypeId !== ATTENDANCE_TYPE_PRESENT ||
            (attendanceTypeId === ATTENDANCE_TYPE_PRESENT && !isOvertime)
          }
        >
          <FormInput
            formControlProps={{
              isRequired: isOvertime,
              onBlur: () => props.onChangedRelatedToSavable(),
            }}
            label="残業承認者"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${matterNo}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${matterNo}.assignedWorkerWorkInfo.overtimeApprovedPerson`}
          />
        </GridItem>
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormTextarea
            label="備考"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${matterNo}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${matterNo}.assignedWorkerWorkInfo.reportMemo`}
          />
        </GridItem>
      </Grid>
      <MatterDetailModal
        isOpen={isOpen}
        matterId={selectedMasterId}
        onClose={() => {
          setSelectedMatterId(undefined);
          onClose();
        }}
      />
    </VStack>
  );
};

export default Matters;
